/**
 * Shared utilities for working with Budget V2 data structures
 */

import { type MonetaryValue } from 'ezmoney';

import {
  type Breakdown,
  type Breakdowns,
} from 'src/core/modules/budgets/budgets-by-dimensions/hooks/useGetBudgetBreakdowns';
import { type BudgetBranch } from 'src/core/modules/budgets/budgets-by-dimensions/models/budgetTree';
import { type Dimension } from 'src/core/modules/budgets/budgets-by-dimensions/models/dimension';
import {
  getDimensionLabel,
  type DimensionData,
} from 'src/core/modules/budgets/budgets-by-dimensions/models/dimensionData';
import { type BudgetBreakdown } from 'src/core/modules/budgets/models/breakdown';

import { type NodePath } from '../../../../budgets-by-dimensions/models/nodePath';

/**
 * Convert V2 breakdown format to legacy breakdown format
 * This allows V2 budgets to work with existing components that expect legacy data
 */
export const convertToLegacyBreakdown = (
  breakdown: Breakdown,
): BudgetBreakdown => ({
  used: breakdown.obligated.used,
  committed: breakdown.obligated.committed,
  usedExceeded: breakdown.exceeded.used,
  committedExceeded: breakdown.exceeded.committed,
  available: breakdown.remaining,
});

/**
 * Type for a flattened budget row ready for display
 */
export type FlattenedBudgetRow = {
  id: string;
  dimension: Dimension | 'root';
  dimensionValue: string | undefined;
  label: string;
  breakdown: BudgetBreakdown;
  allocated: MonetaryValue;
  depth: number;
  hasChildren: boolean;
  nodePath: NodePath;
};

/**
 * Options for flattening a budget tree
 */
export type FlattenBudgetTreeOptions = {
  budgetName: string;
  collapsedIds?: Set<string>;
};

const updateNodePath = (
  nodePath: NodePath,
  dimension: Dimension,
  valueId: string,
): NodePath => {
  switch (dimension) {
    case 'costCenter':
      return { ...nodePath, costCenterId: valueId };
    case 'expenseCategory':
      return { ...nodePath, expenseCategoryId: valueId };
    case 'supplier':
      return { ...nodePath, supplierId: valueId };
    default:
      return { ...nodePath };
  }
};

/**
 * Flatten a V2 budget breakdown tree into an array of displayable rows
 * This handles the recursive tree structure and converts it to a flat list
 * suitable for rendering in a UI
 */
export const flattenBudgetTree = (
  breakdowns: Breakdowns,
  dimensionData: DimensionData,
  options: FlattenBudgetTreeOptions,
): FlattenedBudgetRow[] => {
  const rows: FlattenedBudgetRow[] = [];
  const { budgetName, collapsedIds = new Set<string>() } = options;

  // Add root row
  const rootBreakdown = convertToLegacyBreakdown(breakdowns.outcome);
  rows.push({
    id: 'root',
    dimension: 'root',
    dimensionValue: undefined,
    label: budgetName,
    breakdown: rootBreakdown,
    allocated: breakdowns.outcome.allocated,
    depth: 0,
    hasChildren: Boolean(breakdowns.branch),
    nodePath: {},
  });

  // Recursively traverse branches
  const traverseTree = (
    branch: BudgetBranch<Breakdown> | undefined,
    depth: number,
    parentId: string,
    parentNodePath: NodePath,
  ) => {
    if (!branch) {
      return;
    }

    branch.nodes.forEach((node, index) => {
      const { dimension } = branch;
      const nodeId = `${parentId}-${dimension}-${index}`;
      const nodeBreakdown = convertToLegacyBreakdown(node.outcome);
      const hasChildren = Boolean(node.branch);

      // Get dimension value label using actual dimension data
      const valueId = node.values?.[0];
      const nodePath = updateNodePath(parentNodePath, dimension, valueId ?? '');
      const label = valueId
        ? getDimensionLabel(dimension, valueId, dimensionData)
        : `${dimension} ${index + 1}`;

      rows.push({
        id: nodeId,
        dimension,
        dimensionValue: valueId,
        label,
        breakdown: nodeBreakdown,
        allocated: node.outcome.allocated,
        depth,
        hasChildren,
        nodePath,
      });

      // Recurse into nested dimension if not collapsed
      if (node.branch && !collapsedIds.has(nodeId)) {
        traverseTree(node.branch, depth + 1, nodeId, nodePath);
      }
    });
  };

  // Start traversal if root is not collapsed
  if (!collapsedIds.has('root') && breakdowns.branch) {
    traverseTree(breakdowns.branch, 1, 'root', {});
  }

  return rows;
};
